// Phase 2 — StatusBadge. Maps domain states to design tokens (docs/04 §4.7).
//
// Presentation only: pass the same status string the screens already use; the
// component just picks a token color. An explicit `variant` always wins.
import { Badge, type BadgeProps } from "ui/badge";
import { cn } from "lib/utils";

type Variant = NonNullable<BadgeProps["variant"]>;

// Normalized (lowercased/trimmed) status text -> badge variant.
// Covers the statuses used across inventory, procurement, orders and payments.
const STATUS_MAP: Record<string, Variant> = {
  // positive / done
  completed: "success",
  "stickering completed": "success",
  delivered: "success",
  "in stock": "success",
  "payment success": "success",
  success: "success",
  active: "success",
  paid: "success",
  // in-progress / pending
  "yet to start": "warning",
  "in progress": "warning",
  "in transit": "warning",
  "new orders": "warning",
  "on hold": "warning",
  pending: "warning",
  processing: "warning",
  // negative / terminal
  damaged: "destructive",
  cancelled: "destructive",
  canceled: "destructive",
  "payment failure": "destructive",
  failed: "destructive",
  void: "destructive",
  scrap: "destructive",
  missed: "destructive",
  // neutral
  draft: "secondary",
  new: "accent",
  returned: "secondary",
  sold: "secondary",
  "not required": "secondary",
};

export interface StatusBadgeProps extends Omit<BadgeProps, "children"> {
  status: string;
  /** Force a variant regardless of the status text. */
  variant?: Variant;
}

export function StatusBadge({ status, variant, className, ...props }: StatusBadgeProps) {
  const resolved = variant ?? STATUS_MAP[String(status ?? "").trim().toLowerCase()] ?? "secondary";
  return (
    <Badge variant={resolved} className={cn("gap-1.5", className)} {...props}>
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" aria-hidden />
      {status}
    </Badge>
  );
}
